import React, { useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { BarChart } from '@mui/x-charts/BarChart';
import {
  blueberryTwilightPaletteLight,
  mangoFusionPaletteLight,
} from '@mui/x-charts/colorPalettes';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import {
  Dimension,
  fetchSignupStats,
  selectSignupStats,
  selectSignupStatsStatus,
  selectSignupStatsError,
  SignupSeriesMeta,
} from '../features/signupStats/signupStatsSlice';

type Preset = '7' | '14' | '30' | '180' | '365' | 'custom';

const fmt = (d: Dayjs) => d.format('YYYY-MM-DD');

// Every series of the same UTM value shares one colour so per-building bars of
// a UTM read as one block. "hardware" is explicitly magenta; everything else
// gets a stable palette colour hashed from the UTM value.
const FIXED_UTM_COLORS: Record<string, string> = {
  hardware: '#ff00ff',
};

// The chart's original default colours (teal, blue, ...) from the library's
// palettes, minus the magenta-family entries reserved for "hardware".
const UTM_PALETTE = Array.from(new Set([
  ...blueberryTwilightPaletteLight,
  ...mangoFusionPaletteLight,
])).filter((c) => !['#B800D8', '#DA00FF'].includes(c));

const hashString = (s: string) => {
  let h = 0;
  // Keep the accumulator in safe-integer range without bitwise ops (no-bitwise).
  for (let i = 0; i < s.length; i += 1) h = ((h * 31) + s.charCodeAt(i)) % 2147483647;
  return h;
};

const utmColor = (utm: string) => (
  FIXED_UTM_COLORS[utm] ?? UTM_PALETTE[hashString(utm) % UTM_PALETTE.length]
);

type ChartSeries = { label: string; color: string; data: number[]; stack: string };
type LegendEntry = { utm: string; color: string };

const SignupStatsPage = () => {
  const dispatch = useAppDispatch();
  const [preset, setPreset] = useState<Preset>('30');
  const [dimension, setDimension] = useState<Dimension>('none');
  const today = useMemo(() => dayjs().startOf('day'), []);
  const [customFrom, setCustomFrom] = useState<Dayjs>(today.subtract(29, 'day'));
  const [customTo, setCustomTo] = useState<Dayjs>(today);

  const { from, to } = useMemo(() => {
    if (preset === 'custom') return { from: fmt(customFrom), to: fmt(customTo) };
    const days = parseInt(preset, 10);
    return { from: fmt(today.subtract(days - 1, 'day')), to: fmt(today) };
  }, [preset, customFrom, customTo, today]);

  useEffect(() => {
    dispatch(fetchSignupStats({ from, to, dimension }));
  }, [from, to, dimension, dispatch]);

  const stats = useAppSelector(selectSignupStats({ from, to, dimension }));
  const status = useAppSelector(selectSignupStatsStatus);
  const error = useAppSelector(selectSignupStatsError);

  const { xAxis, series, legend } = useMemo(() => {
    if (!stats) {
      return { xAxis: [] as string[], series: [] as ChartSeries[], legend: [] as LegendEntry[] };
    }
    const dates = stats.buckets.map((b) => b.date);
    const seriesKeys = new Set<string>();
    stats.buckets.forEach((b) => Object.keys(b.series).forEach((k) => seriesKeys.add(k)));

    // series_meta carries the utm/building split for composite keys; fall back
    // to treating the whole key as the utm value (Total dimension, old caches).
    const metaFor = (key: string): SignupSeriesMeta => stats.series_meta?.[key] ?? { utm: key };

    // Group per-building series of the same utm next to each other in the stack.
    const keys = Array.from(seriesKeys).sort((a, b) => {
      const ma = metaFor(a);
      const mb = metaFor(b);
      return ma.utm.localeCompare(mb.utm) || (ma.building ?? '').localeCompare(mb.building ?? '');
    });

    const built = keys.map((key) => {
      const meta = metaFor(key);
      return {
        // The label is what the tooltip shows on bar hover.
        label: meta.building ? `${meta.utm} - ${meta.building}` : meta.utm,
        color: utmColor(meta.utm),
        stack: 'signups',
        data: stats.buckets.map((b) => b.series[key] ?? 0),
      };
    });

    // Legend dedupes to one entry per utm value — buildings only appear in the
    // per-bar tooltip, not above the graph.
    const utms = Array.from(new Set(keys.map((k) => metaFor(k).utm)));
    const legendEntries = utms.map((utm) => ({ utm, color: utmColor(utm) }));

    return { xAxis: dates, series: built, legend: legendEntries };
  }, [stats]);

  return (
    <Box sx={{ p: 3, pt: 10 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>Sign-up Stats</Typography>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }} alignItems="center">
        <ToggleButtonGroup
          exclusive
          size="small"
          value={preset}
          onChange={(_, v) => v && setPreset(v as Preset)}
        >
          <ToggleButton value="7">7d</ToggleButton>
          <ToggleButton value="14">14d</ToggleButton>
          <ToggleButton value="30">30d</ToggleButton>
          <ToggleButton value="180">180d</ToggleButton>
          <ToggleButton value="365">365d</ToggleButton>
          <ToggleButton value="custom">Custom</ToggleButton>
        </ToggleButtonGroup>

        {preset === 'custom' && (
          <Stack direction="row" spacing={1}>
            <DatePicker label="From" value={customFrom} onChange={(v) => v && setCustomFrom(v)} />
            <DatePicker label="To" value={customTo} onChange={(v) => v && setCustomTo(v)} />
          </Stack>
        )}

        <ToggleButtonGroup
          exclusive
          size="small"
          value={dimension}
          onChange={(_, v) => v && setDimension(v as Dimension)}
        >
          <ToggleButton value="none">Total</ToggleButton>
          <ToggleButton value="utm_source">utm_source</ToggleButton>
          <ToggleButton value="utm_medium">utm_medium</ToggleButton>
          <ToggleButton value="utm_campaign">utm_campaign</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {status === 'loading' && !stats ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {legend.length > 0 && (
            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
              {legend.map((entry) => (
                <Stack key={entry.utm} direction="row" spacing={0.75} alignItems="center">
                  <Box sx={{
                    width: 12, height: 12, borderRadius: '2px', bgcolor: entry.color,
                  }}
                  />
                  <Typography variant="body2">{entry.utm}</Typography>
                </Stack>
              ))}
            </Stack>
          )}
          <Box sx={{ height: 460 }}>
            <BarChart
              xAxis={[{ scaleType: 'band', data: xAxis }]}
              series={series}
              height={440}
              tooltip={{ trigger: 'item' }}
              slotProps={{ legend: { hidden: true } }}
              margin={{
                left: 60, right: 20, top: 20, bottom: 60,
              }}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default SignupStatsPage;
